import moment from "moment";
import _ from "lodash";
import { ApiProvider } from "@src/store/ApiProvider";
import { formatCurrency } from "@src/components/utils/numberUtils";

export interface IFulfillmentItem {
	Invoice_ID: number;
	Listing_ID: number;
	Seat_Number: number;
	Item_Price: number;
	Sale_Price: number;
	Buyer_PO_ID?: string | null;
	InHand?: boolean | null;
	Company_Name?: string | null;
	Shipping_Comments?: string | null;
	Exchange_Name?: string | null;
	Sale_Date?: string | null;
	Completed_Rep?: string | null;
	Completed_Date?: string | null;
	Tracking_Number?: string | null;
	Event_Name?: string | null;
	Event_Date?: string | null;
	Event_Time?: string | null;
	Venue_Name?: string | null;
	Section?: string | null;
	Row?: string | null;
}

interface IRolledInvoice {
	Invoice_ID: number;
	Company_Name: string;
	Shipping_Comments: string;
	Exchange_Name: string;
	Buyer_PO_ID: string;
	Sale_Date: string | null;
	Completed_Rep: string;
	Completed_Date: string | null;
	Tracking_Number: string;
	Event_Name: string;
	Event_Date: string | null;
	Event_Time: string;
	Venue_Name: string;
	Section: string;
	Row: string;
	Seat_From: number;
	Seat_Thru: number;
	InHand: boolean;
	Quantity: number;
	Price_Per_Ticket: number;
	Invoice_Amount: number;
}

const rollupSale = (items: Array<IFulfillmentItem>): IRolledInvoice | null => {
	if (!items.length) return null;
	const grouped = _.groupBy(items, (x) => `${x.Invoice_ID}|${x.Listing_ID}`);
	const group = _.first(_.values(grouped)) as Array<IFulfillmentItem>;
	const first = group[0];
	const seats = group.map((x) => x.Seat_Number);

	return {
		Invoice_ID: first.Invoice_ID,
		Company_Name: first.Company_Name ?? "",
		Shipping_Comments: first.Shipping_Comments ?? "",
		Exchange_Name: first.Exchange_Name ?? "",
		Buyer_PO_ID: first.Buyer_PO_ID ?? "",
		Sale_Date: first.Sale_Date ?? null,
		Completed_Rep: first.Completed_Rep ?? "",
		Completed_Date: first.Completed_Date ?? null,
		Tracking_Number: first.Tracking_Number ?? "",
		Event_Name: first.Event_Name ?? "",
		Event_Date: first.Event_Date ?? null,
		Event_Time: first.Event_Time ?? "",
		Venue_Name: first.Venue_Name ?? "",
		Section: first.Section ?? "",
		Row: first.Row ?? "",
		Seat_From: Math.min(...seats),
		Seat_Thru: Math.max(...seats),
		InHand: !!first.InHand,
		Quantity: group.length,
		Price_Per_Ticket: first.Item_Price,
		Invoice_Amount: group.reduce((acc, x) => acc + (x.Sale_Price ?? 0), 0),
	};
};

const fmtDate = (v: string | null): string => (v && moment(v).isValid() ? moment(v).format("MM/DD/YYYY") : "");
const fmtDateTime = (v: string | null): string => (v && moment(v).isValid() ? moment(v).format("MM/DD/YYYY  hh:mm A") : "");
const fmtSeats = (inv: IRolledInvoice): string => {
	const base = inv.Seat_From === inv.Seat_Thru ? `${inv.Seat_From}` : `${inv.Seat_From}-${inv.Seat_Thru}`;
	return inv.InHand ? base : `${base} *`;
};

const buildInvoiceHtml = (inv: IRolledInvoice): string => `
<style> p { margin:0; }  p, div { font-size:0.9em; }  .invoiceReport { font-family: tahoma, arial, verdana, sans-serif; width:600px; }  table { border-collapse:separate; }  th { border:solid 1px #000;padding-left:3px;padding-right:3px; }  h4 { text-align:right; }</style>
<div class="invoiceReport" style="padding:30px 10px 10px 10px;">
	<table style="width:600px;">
		<tr><td><h4>Invoice ID # ${inv.Invoice_ID}</h4></td></tr>
		<tr><td><table style="width:600px;"><tr><td><div style="height:150px;"><div style="width:300px;box-shadow: 5px 5px 5px #888888;padding:5px;border:solid 1px #000;height:90px;"><div style="width:200px;"><p>${inv.Company_Name}</p><p><b>Instructions: </b>${inv.Shipping_Comments}</p></div></div></div></td></tr></table></td></tr>
		<tr><td><table style="width:600px;"><tr><td style="width:300px;border:solid 1px #000;padding:0 5px;"><div>${inv.Exchange_Name} Order # ${inv.Buyer_PO_ID}</div></td><td style="width:320px;border:solid 1px #000;padding:0 5px;"><div>Date of Order ${fmtDateTime(inv.Sale_Date)}</div></td></tr></table></td></tr>
		<tr><td><table style="width:600px;"><tr><td style="width:300px;border:solid 1px #000;padding:0 5px;"><div>Salesperson ${inv.Completed_Rep}</div></td><td style="width:320px;border:solid 1px #000;padding:0 5px;"><div>Date Completed ${fmtDateTime(inv.Completed_Date)}</div></td></tr></table></td></tr>
		<tr><td><table style="width:600px;"><tr><td style="width:300px;"></td><td style="width:320px;border:solid 1px #000;padding:0 5px;"><div>Tracking Number ${inv.Tracking_Number}</div></td></tr></table></td></tr>
		<tr><td><table style="margin-top:50px;">
			<tr><th style="width:120px;"><div>Section</div></th><th style="width:120px;"><div>Row</div></th><th style="width:100px;"><div>Seat(s)</div></th><th style="width:40px;"><div>Qty</div></th><th style="width:100px;"><div>Price/Tic</div></th><th style="width:100px;"><div>Price</div></th></tr>
			<tr><td colspan="5"><div>${inv.Event_Name} ${fmtDate(inv.Event_Date)} ${inv.Event_Time} ${inv.Venue_Name}</div></td></tr>
			<tr><td><div>${inv.Section}</div></td><td><div>${inv.Row}</div></td><td><div style="text-align:center">${fmtSeats(inv)}</div></td><td><div style="text-align:center">${inv.Quantity}</div></td><td><div style="text-align:right">${formatCurrency(inv.Price_Per_Ticket)}</div></td><td><div style="text-align:right">${formatCurrency(inv.Invoice_Amount)}</div></td></tr>
		</table></td></tr>
	</table>
</div>`;

export const generateInvoicePdfUrl = async (invoiceId: number): Promise<string | null> => {
	try {
		const items = await ApiProvider.default.post<Array<IFulfillmentItem>>("/api/Fulfillment/QueryFulfillment", {
			InvoiceId: invoiceId,
			ForceRefresh: true,
		});

		const invoice = rollupSale(items ?? []);
		if (!invoice) return null;

		const html = buildInvoiceHtml(invoice);
		const fileName = await ApiProvider.default.post<string>("/api/System/CreatePDFFromHtml", {
			PDFInfo: [{ Id: invoiceId, Html: html }],
		});

		if (!fileName) return null;

		const printName = `Invoice_Report_${invoiceId}.pdf`;
		return `${ApiProvider.default.baseURL}/api/System/GetFile?fileName=${encodeURIComponent(
			fileName,
		)}&newFileName=${encodeURIComponent(printName)}`;
	} catch {
		return null;
	}
};
